import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Radio, Gauge, Clock, ExternalLink } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

export default function GPS() {
  const { t, lang, fmtNumber } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const [cars, setCars] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ lat: "", lng: "", mileage: "" });

  const load = async () => { const { data } = await api.get("/cars"); setCars(data); };
  useEffect(() => { load(); }, []);

  const openUpdate = (car) => {
    setSelected(car);
    setForm({ lat: car.last_lat ?? "", lng: car.last_lng ?? "", mileage: car.current_mileage ?? "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/cars/${selected.id}/gps`, {
        lat: Number(form.lat), lng: Number(form.lng),
        mileage: form.mileage ? Number(form.mileage) : null,
      });
      toast.success(t("gps.locationUpdated"));
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="gps-page">
      <div>
        <div className="overline">{t("gps.pageTitle", { n: cars.filter((c) => c.gps_device_id).length })}</div>
        <p className="text-sm text-zinc-500 mt-1">{t("gps.pageSubtitle")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-0 border-l border-t border-zinc-200">
        {cars.map((c) => (
          <div key={c.id} className="border-r border-b border-zinc-200 -ml-px -mt-px bg-white p-6 space-y-4" data-testid={`gps-card-${c.plate}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg tracking-tight">{c.model}</div>
                <div className="text-xs text-zinc-500 font-mono">{c.plate}</div>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider">
                <Radio className={`w-3 h-3 ${c.gps_device_id ? "text-green-600" : "text-zinc-300"}`} />
                {c.gps_device_id ? t("gps.active") : t("gps.noGps")}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <Row icon={Radio} label={t("gps.device")} value={c.gps_device_id || "—"} mono />
              <Row icon={MapPin} label={t("gps.latitude")} value={c.last_lat !== null && c.last_lat !== undefined ? c.last_lat.toFixed(6) : "—"} mono />
              <Row icon={MapPin} label={t("gps.longitude")} value={c.last_lng !== null && c.last_lng !== undefined ? c.last_lng.toFixed(6) : "—"} mono />
              <Row icon={Gauge} label={t("gps.odometer")} value={`${fmtNumber(c.current_mileage || 0)} mi`} mono />
              <Row icon={Clock} label={t("gps.updated")} value={c.last_location_update ? formatDistanceToNow(parseISO(c.last_location_update), { addSuffix: true, locale: dateLocale }) : t("common.never")} />
            </div>

            <div className="flex gap-2 pt-3 border-t border-zinc-100">
              <Button onClick={() => openUpdate(c)} className="flex-1 rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white" data-testid={`update-gps-${c.plate}`}>
                {t("gps.updateBtn")}
              </Button>
              {c.last_lat && c.last_lng && (
                <a href={`https://www.google.com/maps?q=${c.last_lat},${c.last_lng}`} target="_blank" rel="noreferrer"
                   className="px-3 inline-flex items-center justify-center border border-zinc-200 hover:border-zinc-400"
                   data-testid={`open-map-${c.plate}`}><ExternalLink className="w-4 h-4" /></a>
              )}
            </div>
          </div>
        ))}
        {cars.length === 0 && (
          <div className="col-span-full p-16 text-center border-r border-b border-zinc-200 -ml-px -mt-px">
            <MapPin className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
            <div className="overline">{t("gps.noVehicles")}</div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{t("gps.updateTitle")} · {selected?.plate}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("gps.latitude")}><Input type="number" step="any" className="rounded-none" required value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="40.7128" data-testid="gps-lat-input" /></Field>
              <Field label={t("gps.longitude")}><Input type="number" step="any" className="rounded-none" required value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="-74.0060" data-testid="gps-lng-input" /></Field>
            </div>
            <Field label={t("gps.odometerInput")}><Input type="number" className="rounded-none" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} /></Field>
            <div className="text-xs text-zinc-500">{t("gps.hint")}</div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
              <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-gps-button">{t("common.save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><label className="overline">{label}</label>{children}</div>
);

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <div className="flex items-center gap-2 text-zinc-500"><Icon className="w-3 h-3" /> {label}</div>
      <div className={mono ? "font-mono" : ""}>{value}</div>
    </div>
  );
}
